import { useState, useCallback } from "react";
import stepsStyles from "../steps.module.css";
import { FieldError } from "../../../FieldHelpers";
import { REGEX_PLANO, REGEX_PLANO_Z, REGEX_FOLIO } from "../../../../services/validationRules";
import apiFetch from "../../../../../../shared/services/apiClient";

const FIELD_LABELS = {
  idFilial: "ID de Filial",
  numPlano: "Número de Plano",
  numFolio: "Número de Folio",
};

// ── Hook: verifica si un campo ya existe en el condominio ──────────────────
function useUniquenessCheck() {
  const [checking, setChecking] = useState({});
  const [dupErrors, setDupErrors] = useState({});

  const check = useCallback(async (field, value) => {
    if (!value || value.trim() === "") return;
    setChecking((prev) => ({ ...prev, [field]: true }));
    try {
      const res = await apiFetch(
        `/api/residents/filial-check/?${field}=${encodeURIComponent(value.trim())}`,
      );
      const data = await res.json();
      setDupErrors((prev) => ({
        ...prev,
        [field]: data[field]
          ? `Este ${FIELD_LABELS[field]} ya está registrado en el condominio.`
          : null,
      }));
    } catch (_) {
      // Si falla el check no bloquear — el backend lo rechazará al guardar
    } finally {
      setChecking((prev) => ({ ...prev, [field]: false }));
    }
  }, []);

  return {
    check,
    checking,
    dupErrors,
    clearDup: (field) => setDupErrors((prev) => ({ ...prev, [field]: null })),
  };
}

export default function Filial({ formData, setFormData, errors = {} }) {
  const { check, checking, dupErrors, clearDup } = useUniquenessCheck();
  const [autoGenerado, setAutoGenerado] = useState(
    REGEX_PLANO_Z.test((formData.filial?.numPlano || "").trim())
  );
  const [generando, setGenerando] = useState(false);

  function handleChange(field, value) {
    setFormData((prev) => ({
      ...prev,
      filial: { ...prev.filial, [field]: value },
    }));
    clearDup(field);
  }

  function handlePlanoChange(e) {
    // Si el código es automático (Z-) el campo es read-only; ignorar cambios manuales
    if (autoGenerado) return;
    let raw = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, "");
    let fmt = raw;
    if (raw.length > 1) fmt = raw[0] + "-" + raw.slice(1);
    if (raw.length > 6)
      fmt = raw[0] + "-" + raw.slice(1, 6) + "-" + raw.slice(6);
    handleChange("numPlano", fmt.slice(0, 12));
  }

  async function generarCodigoAuto() {
    setGenerando(true);
    try {
      const res = await apiFetch("/api/residents/filial/siguiente-sin-plano/");
      const data = await res.json();
      handleChange("numPlano", data.numPlano);
      setAutoGenerado(true);
      clearDup("numPlano");
    } catch (_) {
      // Si falla, el usuario puede intentar de nuevo
    } finally {
      setGenerando(false);
    }
  }

  function limpiarPlano() {
    handleChange("numPlano", "");
    setAutoGenerado(false);
    clearDup("numPlano");
  }

  function handleFolioChange(e) {
    const digits = e.target.value.replace(/[^0-9]/g, "").slice(0, 9);
    let out = "";
    if (digits.length === 0) out = "";
    else if (digits.length === 1) out = digits;
    else if (digits.length <= 6) out = digits[0] + "-" + digits.slice(1);
    else out = digits[0] + "-" + digits.slice(1, 6) + "-F-" + digits.slice(6);
    handleChange("numFolio", out);
  }

  const planoVal = (formData.filial.numPlano || "").trim();
  const planoOk  = REGEX_PLANO.test(planoVal) || REGEX_PLANO_Z.test(planoVal);
  const folioOk = REGEX_FOLIO.test((formData.filial.numFolio || "").trim());

  // Combinar error de validación + error de duplicado por campo
  const err = (field) => errors[field] || dupErrors[field];

  const CheckSpinner = ({ field }) =>
    checking[field] ? (
      <i
        className="fa-solid fa-circle-notch fa-spin"
        style={{
          position: "absolute",
          right: 10,
          top: "50%",
          transform: "translateY(-50%)",
          color: "#60a5fa",
          fontSize: "0.75rem",
        }}
      />
    ) : null;

  const OkIcon = ({ field, formatOk }) =>
    formatOk &&
    formData.filial[field] &&
    !dupErrors[field] &&
    !checking[field] ? (
      <i
        className="fa-solid fa-circle-check"
        style={{
          position: "absolute",
          right: 10,
          top: "50%",
          transform: "translateY(-50%)",
          color: "#4ade80",
          fontSize: "0.75rem",
        }}
      />
    ) : null;

  return (
    <div className="step">
      <h3 className="p-0 mt-2 mb-0 title" style={{fontSize:"1.3rem"}}>Información de la Filial</h3>
      <p className={`${stepsStyles["inactive"]} mb-3` } style={{fontSize:"1rem"}}>Inserte los siguientes detalles para continuar.</p>

      <div className={stepsStyles["form-wrapper"]}>
        {/* ── ID Filial — OBLIGATORIO ────────────────────────────────── */}
        <div className="form-group">
          <label className="form-label">
            ID Filial <span style={{ color: "#f87171" }}>*</span>
          </label>
          <div style={{ position: "relative" }}>
            <input
              name="idFilial"
              type="text"
              className={`${stepsStyles["form-control"]} form-control`}
              style={{ borderColor: err("idFilial") ? "#ef4444" : undefined }}
              placeholder="Ejemplo: A01"
              value={formData.filial.idFilial}
              onChange={(e) => handleChange("idFilial", e.target.value)}
              onBlur={() => check("idFilial", formData.filial.idFilial)}
            />
            <CheckSpinner field="idFilial" />
            {formData.filial.idFilial &&
              !err("idFilial") &&
              !checking.idFilial && (
                <i
                  className="fa-solid fa-circle-check"
                  style={{
                    position: "absolute",
                    right: 10,
                    top: "50%",
                    transform: "translateY(-50%)",
                    color: "#4ade80",
                    fontSize: "0.75rem",
                  }}
                />
              )}
          </div>
          <FieldError msg={err("idFilial")} />
        </div>

        {/* ── Número Plano y Folio ───────────────────────────────────── */}
        <div className="form-group row g-3 g-md-4">
          <div className="col-12 col-md-6">
            <label className="form-label">
              Número Plano{" "}
              <span style={{ color: "var(--text-muted)", fontWeight: 400, fontSize: "0.8rem" }}>
                (opcional)
              </span>
            </label>

            {/* Botones: Generar o Limpiar */}
            <div style={{ display: "flex", gap: 6, marginBottom: 6 }}>
              {!autoGenerado ? (
                <button
                  type="button"
                  onClick={generarCodigoAuto}
                  disabled={generando}
                  style={{
                    fontSize: "0.72rem",
                    padding: "3px 10px",
                    borderRadius: 6,
                    border: "1px solid var(--border-card)",
                    background: "var(--bg-card)",
                    color: "var(--text-secondary)",
                    cursor: generando ? "not-allowed" : "pointer",
                    display: "flex",
                    alignItems: "center",
                    gap: 5,
                    opacity: generando ? 0.7 : 1,
                  }}
                >
                  {generando ? (
                    <i className="fa-solid fa-circle-notch fa-spin" style={{ fontSize: "0.7rem" }} />
                  ) : (
                    <i className="fa-solid fa-wand-magic-sparkles" style={{ fontSize: "0.7rem", color: "#a78bfa" }} />
                  )}
                  Generar código automático
                </button>
              ) : (
                <button
                  type="button"
                  onClick={limpiarPlano}
                  style={{
                    fontSize: "0.72rem",
                    padding: "3px 10px",
                    borderRadius: 6,
                    border: "1px solid #f87171",
                    background: "transparent",
                    color: "#f87171",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    gap: 5,
                  }}
                >
                  <i className="fa-solid fa-xmark" style={{ fontSize: "0.7rem" }} />
                  Limpiar
                </button>
              )}
            </div>

            <div style={{ position: "relative" }}>
              <input
                name="numPlano"
                type="text"
                className={`${stepsStyles["form-control"]} form-control`}
                style={{
                  borderColor: err("numPlano")
                    ? "#ef4444"
                    : planoOk && !dupErrors.numPlano && formData.filial.numPlano
                      ? "#4ade80"
                      : undefined,
                  paddingRight: autoGenerado ? 60 : undefined,
                  background: autoGenerado ? "var(--bg)" : undefined,
                  color: autoGenerado ? "var(--text-muted)" : undefined,
                  cursor: autoGenerado ? "default" : undefined,
                }}
                value={formData.filial.numPlano}
                onChange={handlePlanoChange}
                onBlur={() => {
                  if (planoOk && !autoGenerado) check("numPlano", formData.filial.numPlano);
                }}
                placeholder="A-00000-2020"
                maxLength={autoGenerado ? 15 : 12}
                readOnly={autoGenerado}
              />
              {/* Ícono de candado cuando es auto-generado */}
              {autoGenerado && (
                <i
                  className="fa-solid fa-lock"
                  style={{
                    position: "absolute",
                    right: 28,
                    top: "50%",
                    transform: "translateY(-50%)",
                    color: "#a78bfa",
                    fontSize: "0.7rem",
                  }}
                />
              )}
              <CheckSpinner field="numPlano" />
              <OkIcon field="numPlano" formatOk={planoOk} />
            </div>
            <div style={{ fontSize: "0.72rem", color: "var(--text-muted)", marginTop: 3 }}>
              {autoGenerado
                ? "Código automático generado — sin número de plano catastral"
                : "Formato: Letra-5dígitos-Año (ej: A-10101-2020)"}
            </div>
            <FieldError msg={err("numPlano")} />
          </div>

          <div className="col-12 col-md-6">
            <label className="form-label">
              Número Folio <span style={{ color: "#f87171" }}>*</span>
            </label>
            <div style={{ position: "relative" }}>
              <input
                name="numFolio"
                type="text"
                className={`${stepsStyles["form-control"]} form-control`}
                style={{
                  borderColor: err("numFolio")
                    ? "#ef4444"
                    : folioOk && !dupErrors.numFolio
                      ? "#4ade80"
                      : undefined,
                }}
                value={formData.filial.numFolio}
                onChange={handleFolioChange}
                onBlur={() => {
                  if (folioOk) check("numFolio", formData.filial.numFolio);
                }}
                placeholder="1-10101-F-000"
                maxLength={13}
              />
              <CheckSpinner field="numFolio" />
              <OkIcon field="numFolio" formatOk={folioOk} />
            </div>
            <div
              style={{ fontSize: "0.72rem", color: "var(--text-muted)", marginTop: 3 }}
            >
              Formato: Dígito-5dígitos-F-3dígitos (ej: 1-10101-F-000)
            </div>
            <FieldError msg={err("numFolio")} />
          </div>
        </div>

        {/* ── Tipo de Propiedad ──────────────────────────────────────── */}
        <div className="form-group">
          <label className="form-label">
            Tipo de Propiedad <span style={{ color: "#f87171" }}>*</span>
          </label>
          <select
            name="tipoPropiedad"
            className={`${stepsStyles["form-select"]} form-select ${stepsStyles["form-control"]} form-control`}
            style={{
              borderColor: errors.tipoPropiedad ? "#ef4444" : undefined,
            }}
            value={formData.filial.tipoPropiedad}
            onChange={(e) => handleChange("tipoPropiedad", e.target.value)}
          >
            <option value="">— Seleccioná el tipo —</option>
            <option value="Construida">Construida</option>
            <option value="Lote Vacio">Lote vacío</option>
            <option value="En Construccion">En construcción</option>
          </select>
          <FieldError msg={errors.tipoPropiedad} />
        </div>
      </div>
    </div>
  );
}
